import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { Preloader } from "@/components";
import { ChatMessage } from "@/features/chats";
import { useAdminChats } from "../model/AdminChatsContext";
import { useAdminChatOutlet } from "./AdminChatUserLayout";

import styles from "./AdminChatMessagesPage.module.scss";

const formatDate = (value: string) => {
  try {
    const date = new Date(value);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    return value;
  }
};

const isOutgoing = (author: string) =>
  author === "trainer-user" || author === "support-user";

// Функция для валидации изображения
const validateImageFile = (file: File): string | null => {
  // Проверяем тип файла
  const validImageTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  
  if (!validImageTypes.includes(file.type)) {
    return "Разрешены только изображения (JPEG, PNG, GIF, WebP)";
  }
  
  // Проверяем размер файла (например, максимум 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return "Размер файла не должен превышать 10MB";
  }
  
  return null;
};

// Функция для парсинга и отображения контента с тегами <img>
const renderMessageContent = (content: string) => {
  const imgTagRegex = /<img>([^<]+)<\/img>/g;
  const parts: Array<{ type: "text" | "image"; content: string }> = [];
  let lastIndex = 0;
  let match;

  while ((match = imgTagRegex.exec(content)) !== null) {
    // Добавляем текст до тега
    if (match.index > lastIndex) {
      const textPart = content.substring(lastIndex, match.index);
      if (textPart.trim()) {
        parts.push({ type: "text", content: textPart });
      }
    }
    
    // Добавляем изображение
    parts.push({ type: "image", content: match[1] });
    lastIndex = imgTagRegex.lastIndex;
  }
  
  // Добавляем оставшийся текст
  if (lastIndex < content.length) {
    const textPart = content.substring(lastIndex);
    if (textPart.trim()) {
      parts.push({ type: "text", content: textPart });
    }
  }
  
  // Если не было найдено тегов, возвращаем весь текст
  if (parts.length === 0) {
    parts.push({ type: "text", content });
  }
  
  return parts;
};

export const AdminChatMessagesPage = () => {
  const { userId, profile } = useAdminChatOutlet();
  const { getMessages, getCachedMessages, sendSupportMessage, isSending } =
    useAdminChats();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const cachedMessages = getCachedMessages(userId);
  const messages = cachedMessages ?? [];

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.of_date).getTime() - new Date(b.of_date).getTime(),
  );

  // Блокируем скролл body когда чат открыт
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);

      try {
        const result = await getMessages(userId);

        if (result === null) {
          navigate("..", { replace: true });
        } else {
          // Скролл вниз после загрузки сообщений
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: "auto" });
            }
          }, 100);
        }
      } finally {
        setLoading(false);
      }
    };

    if (cachedMessages === undefined && !isLoading) {
      loadMessages();
    }
  }, [cachedMessages, getMessages, isLoading, navigate, userId]);

  // Автоскролл вниз при изменении сообщений
  useEffect(() => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + 100; // 100px threshold

      // Скроллим только если пользователь уже был внизу
      if (isNearBottom || sortedMessages.length === 0) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    }
  }, [sortedMessages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateImageFile(file);
    if (error) {
      alert(error);
      // Сбрасываем input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setSelectedImage(file);
    
    // Создаем превью
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async () => {
    const trimmed = draft.trim();
    const hasImage = selectedImage !== null;
    
    if (!trimmed && !hasImage) {
      return;
    }

    const messageText = trimmed || undefined;
    const imageFile = selectedImage || undefined;

    // Очищаем поля перед отправкой
    setDraft("");
    if (hasImage) {
      handleRemoveImage();
    }
    
    // Отправляем сообщение с текстом и/или изображением
    await sendSupportMessage(userId, messageText, imageFile);
    
    // Обновляем список сообщений после отправки
    await getMessages(userId, true);
    
    // Скролл после отправки
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 200);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setFullscreenImage(imageUrl);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && fullscreenImage) {
        handleCloseFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (fullscreenImage) {
        document.body.style.overflow = "";
      }
    };
  }, [fullscreenImage]);

  return (
    <div className={styles.wrapper}>
      {/* Фиксированный header */}
      <div className={styles.header}>
        <button
          onClick={() => navigate(`/admin/chats/${userId}`)}
          className={styles.backButton}
        >
          ←
        </button>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {profile.fullname
              ? profile.fullname.charAt(0).toUpperCase()
              : "?"}
          </div>
          <div className={styles.userMeta}>
            <span className={styles.userName}>{profile.fullname}</span>
            {profile.username && (
              <span className={styles.userUsername}>@{profile.username}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate("../logs")}
          className={styles.logsButton}
        >
          Логи
        </button>
      </div>

      {/* Область сообщений */}
      <div className={styles.messagesContainer} ref={messagesContainerRef}>
        {isLoading ? (
          <div className={styles.loader}>
            <Preloader />
          </div>
        ) : (
          <>
            {!sortedMessages.length && (
              <div className={styles.empty}>Сообщений пока нет</div>
            )}

            {sortedMessages.map((message: ChatMessage) => {
              const outgoing = isOutgoing(message.author);
              const content = message.content || "";
              const contentParts = renderMessageContent(content);

              return (
                <div
                  key={message.id}
                  className={clsx(
                    styles.message,
                    outgoing ? styles.outgoing : styles.incoming,
                  )}
                >
                  <div className={styles.bubble}>
                    <div className={styles.content}>
                      {contentParts.length > 0 ? (
                        contentParts.map((part, index) => {
                          if (part.type === "image") {
                            return (
                              <div key={index} className={styles.imageContainer}>
                                <img
                                  src={part.content}
                                  alt="Изображение"
                                  className={styles.messageImage}
                                  onClick={() => handleImageClick(part.content)}
                                  onError={(e) => {
                                    // Если изображение не загрузилось, скрываем его
                                    (e.target as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              </div>
                            );
                          } else {
                            const contentLines = part.content.split("\n");
                            return contentLines.map((line, lineIndex) => (
                              <p key={`${index}-${lineIndex}`}>
                                {line || "\u00A0"}
                              </p>
                            ));
                          }
                        })
                      ) : (
                        <p>Сообщение пустое</p>
                      )}
                    </div>
                    <span className={styles.date}>
                      {formatDate(message.of_date)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Фиксированный input снизу */}
      <div className={styles.composer}>
        {imagePreview && (
          <div className={styles.imagePreview}>
            <img src={imagePreview} alt="Превью" className={styles.previewImage} />
            <button
              type="button"
              className={styles.removeImageButton}
              onClick={handleRemoveImage}
              aria-label="Удалить изображение"
            >
              ×
            </button>
          </div>
        )}
        <div className={styles.inputWrapper}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageSelect}
            className={styles.fileInput}
            id="image-upload"
            disabled={isSending}
          />
          <label htmlFor="image-upload" className={styles.imageButton}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 8.5L11.5 12.5C10.6716 13.3284 9.32843 13.3284 8.5 12.5C7.67157 11.6716 7.67157 10.3284 8.5 9.5L12.5 5.5C13.3284 4.67157 14.6716 4.67157 15.5 5.5C16.3284 6.32843 16.3284 7.67157 15.5 8.5ZM15.5 8.5L13.5 10.5M11.5 12.5L9.5 14.5C8.67157 15.3284 7.32843 15.3284 6.5 14.5C5.67157 13.6716 5.67157 12.3284 6.5 11.5L10.5 7.5C11.3284 6.67157 12.6716 6.67157 13.5 7.5C14.3284 8.32843 14.3284 9.67157 13.5 10.5M13.5 10.5L11.5 12.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </label>
          <textarea
            className={styles.textarea}
            placeholder="Напишите сообщение..."
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={1}
          />
          <button
            type="button"
            className={styles.sendButton}
            onClick={handleSendMessage}
            disabled={isSending || (!draft.trim() && !selectedImage)}
          >
            {isSending ? "..." : "→"}
          </button>
        </div>
      </div>

      {/* Модальное окно для полноэкранного просмотра изображения */}
      {fullscreenImage && (
        <div className={styles.fullscreenModal} onClick={handleCloseFullscreen}>
          <button
            className={styles.closeButton}
            onClick={handleCloseFullscreen}
            aria-label="Закрыть"
          >
            ×
          </button>
          <img
            src={fullscreenImage}
            alt="Полноэкранное изображение"
            className={styles.fullscreenImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
